//
//  router.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 22, 2026
//  Author: Bob Keathley
//
//  Hash router for the consoles (#/fleet, #/runs/...). No dependency; the back
//  button works because the hash changes. Replace only if views need nesting.
//
// Hash router: `#/fleet`, `#/runs`, `#/runs/<session_id>`, `#/workstreams`, `#/notebooks/<id>/deeds`,
// `#/users`… No dependency; the browser history and
// back button work because the hash changes. Replace with a real router when views need nesting.
import { useEffect, useState } from 'react'

export type Route = { path: string; parts: string[]; query: URLSearchParams }

export function parseHash(hash: string): Route {
  const raw = (hash || '#/').replace(/^#/, '')
  const [p, q = ''] = raw.split('?')
  const path = p.startsWith('/') ? p : `/${p}`
  return { path, parts: path.split('/').filter(Boolean), query: new URLSearchParams(q) }
}

export function navigate(path: string) {
  const target = `#${path.startsWith('/') ? path : `/${path}`}`
  if (location.hash !== target) location.hash = target
}

export function href(path: string) {
  return `#${path.startsWith('/') ? path : `/${path}`}`
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(typeof location !== 'undefined' ? location.hash : '#/'))
  useEffect(() => {
    const on = () => setRoute(parseHash(location.hash))
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])
  return route
}
